import { useMemo, useEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { ChartTypeRenderer, type ChartType } from "@phoenix/pages/project/metrics/ChartTypeRenderer";
import type { TopUsersByTokenQuery } from "./__generated__/TopUsersByTokenQuery.graphql";

const FALLBACK_LABEL = "(unknown)";
const MAX_ITEMS = 10;
const COLOR_PALETTE = ["#10b981", "#059669", "#047857", "#34d399", "#6ee7b7", "#a7f3d0", "#065f46", "#d1fae5", "#0d9488", "#14b8a6"];

function fmtTokens(v: number) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
}

interface Props extends ProjectMetricViewProps {
  chartType?: ChartType;
}

export function TopUsersByToken({ projectId, timeRange, chartType = "vertical_bar", widgetId, widgetTitle, onDataExport }: Props) {
  const data = useLazyLoadQuery<TopUsersByTokenQuery>(
    graphql`
      query TopUsersByTokenQuery($projectId: ID!, $timeRange: TimeRange!) {
        project: node(id: $projectId) {
          ... on Project {
            spans(timeRange: $timeRange, rootSpansOnly: true, first: 500) {
              edges {
                span: node {
                  username
                  cumulativeTokenCountTotal
                }
              }
            }
          }
        }
      }
    `,
    {
      projectId,
      timeRange: {
        start: timeRange.start?.toISOString(),
        end: timeRange.end?.toISOString(),
      },
    }
  );

  const chartData = useMemo(() => {
    const edges = data.project?.spans?.edges ?? [];
    const map: Record<string, number> = {};
    for (const { span } of edges) {
      const key = span.username ?? FALLBACK_LABEL;
      map[key] = (map[key] ?? 0) + (span.cumulativeTokenCountTotal ?? 0);
    }
    return Object.entries(map)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, MAX_ITEMS);
  }, [data]);

  useEffect(() => {
    if (chartData.length > 0 && onDataExport && widgetId && widgetTitle) {
      onDataExport(widgetId, widgetTitle, chartData);
    }
  }, [chartData, onDataExport, widgetId, widgetTitle]);

  return (
    <ChartTypeRenderer
      data={chartData}
      chartType={chartType}
      valueFormatter={fmtTokens}
      colorPalette={COLOR_PALETTE}
      emptyIcon="👤"
      emptyText="No user token data"
    />
  );
}
